import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Container from "@mui/material/Container";
import { alpha, keyframes } from "@mui/material/styles";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from "react";

import { StyledAnchor } from "@/components/common/styles";
import {
  ACCENT_DEEP,
  ACCENT_TINT,
  FRAME,
  marketingPalette,
} from "@/components/marketing/tokens";
import { ChevronIcon } from "./navIcons";
import NavMenuContent from "./NavMenuContent";
import { NAV_MENUS, PRICING_LINK, type NavMenu } from "./navigation";
import { BAR_ROW, GUTTER, PANEL_MOTION } from "./tokens";

/** Shared by the four disclosures and by Pricing, which has no menu behind it. */
const TOP_LEVEL = {
  display: "flex",
  alignItems: "center",
  justifyContent: "normal",
  columnGap: "6.08px",
  height: 40,
  p: 2,
  borderRadius: "8px",
  backgroundColor: "background.default",
  // `ButtonBase` leaves the UA's own button font in place.
  fontFamily: "inherit",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "24px",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: ACCENT_TINT },
};

/** No menu's id, so nothing is mounted until one is chosen. */
const CLOSED = { id: "", open: false };

const drop = keyframes({
  from: { opacity: 0, transform: "translateY(-100%)" },
  to: { opacity: 1, transform: "none" },
});

const lift = keyframes({
  from: { opacity: 1, transform: "none" },
  to: { opacity: 0, transform: "translateY(-100%)" },
});

/** The columns' own exit: the reverse of their arrival, four times faster. */
const fall = keyframes({
  from: { opacity: 1, transform: "none" },
  to: { opacity: 0, transform: `translateY(-${PANEL_MOTION.rise}px)` },
});

/**
 * The full-bleed panel under the bar. It is positioned against the bar rather
 * than against the content column, because the white it paints runs the whole
 * width of the viewport while its contents stay in the column.
 *
 * The curtain is a layer of its own: the published panel slides white down from
 * behind the bar and only then fades its columns up, which one element cannot
 * do. Clipping it is what makes the slide read as a curtain, and the padding
 * below it is what leaves room for its shadow inside that clip.
 *
 * The two layers part company on the way back. The columns go in 125ms and the
 * curtain in 400ms, so nothing is left standing over the page while the white
 * retracts. Riding the curtain instead would drag the columns up the viewport.
 */
function Panel({
  id,
  labelledBy,
  menu,
  open,
  onExited,
  onNavigate,
}: {
  id: string;
  labelledBy: string;
  menu: NavMenu;
  open: boolean;
  onExited: () => void;
  onNavigate: () => void;
}) {
  return (
    <Box
      id={id}
      aria-labelledby={labelledBy}
      sx={{
        position: "absolute",
        top: `${BAR_ROW.desktop}px`,
        left: 0,
        right: 0,
        minWidth: 1,
        pb: 6,
        overflow: "hidden",
      }}
    >
      <Box
        onAnimationEnd={open ? undefined : onExited}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: "32px",
          backgroundColor: "background.default",
          borderBottom: `1px solid ${alpha(marketingPalette.ink, 0.07)}`,
          boxShadow: `${alpha(marketingPalette.ink, 0.07)} 0px 10px 30px -9px`,
          animation: `${open ? drop : lift} ${PANEL_MOTION.duration}ms ease-out both`,
        }}
      />
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          position: "relative",
          maxWidth: `${FRAME.header}px`,
          px: `${GUTTER}px`,
          pt: 5,
          ...(open
            ? undefined
            : {
                animation: `${fall} ${PANEL_MOTION.exit}ms ${PANEL_MOTION.exitEase} both`,
              }),
        }}
      >
        <NavMenuContent menu={menu} variant="panel" onNavigate={onNavigate} />
      </Container>
    </Box>
  );
}

/** An open disclosure wears the accent; a hovered one only takes its tint. */
function toggleSx(open: boolean) {
  return {
    ...TOP_LEVEL,
    backgroundColor: open ? ACCENT_TINT : "background.default",
    color: open ? ACCENT_DEEP : "text.primary",
  };
}

/** The chevron turns over faster on the way back than on the way down. */
function chevronSx(open: boolean) {
  return {
    fontSize: "14.08px",
    transform: open ? "rotate(180deg)" : "none",
    transition: `transform ${open ? 200 : 100}ms ease`,
  };
}

function MenuDisclosure({
  menu,
  baseId,
  mounted,
  open,
  onToggle,
  onClose,
  onExited,
}: {
  menu: NavMenu;
  baseId: string;
  mounted: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onExited: () => void;
}) {
  const toggleId = `${baseId}-${menu.id}`;
  const panelId = `${toggleId}-panel`;
  return (
    <Box sx={{ p: "16px 12.8px" }}>
      <ButtonBase
        id={toggleId}
        aria-expanded={open}
        aria-controls={mounted ? panelId : undefined}
        onClick={onToggle}
        sx={toggleSx(open)}
      >
        {menu.label}
        <ChevronIcon sx={chevronSx(open)} />
      </ButtonBase>
      {mounted ? (
        <Panel
          id={panelId}
          labelledBy={toggleId}
          menu={menu}
          open={open}
          onExited={onExited}
          onNavigate={onClose}
        />
      ) : null}
    </Box>
  );
}

/**
 * The bar's five top-level items at 992 and up.
 *
 * The published site opens these on click, not on hover, its dropdowns carry
 * `data-hover="false"`, and closes them on Escape, on a second click, on a
 * click outside and on choosing another one. Pointer-leave does not close them.
 *
 * Two things here are deliberately better than the published markup. Its
 * toggles are `<div role="button">` with `outline: 0`, so a keyboard user gets
 * no ring at all; these are real buttons and take the ring `MarketingShell`
 * hands back to the browser. And the published panel stays open when focus tabs
 * out of it into the rest of the bar, stranding the disclosure behind the user;
 * this one closes.
 */
export default function DesktopNav() {
  const baseId = useId();
  const [panel, setPanel] = useState(CLOSED);
  const groupRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setPanel((current) => ({ ...current, open: false }));
  }, []);

  useEffect(() => {
    if (!panel.open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!groupRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [close, panel.open]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      groupRef.current
        ?.querySelector<HTMLButtonElement>('[aria-expanded="true"]')
        ?.focus();
      close();
    }
  };

  const onBlur = (event: FocusEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      close();
    }
  };

  return (
    <Box
      ref={groupRef}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      sx={{
        display: "flex",
        alignItems: "center",
        height: `${BAR_ROW.desktop}px`,
      }}
    >
      {NAV_MENUS.map((menu) => {
        const isThisMenu = panel.id === menu.id;
        return (
          <MenuDisclosure
            key={menu.id}
            menu={menu}
            baseId={baseId}
            mounted={isThisMenu}
            open={isThisMenu && panel.open}
            // Not the negation of `open`: while this panel is already lifting
            //, this menu, `open` already false, a click writes back the state
            // it has, so it does nothing. It stays closed and `onExited`
            // finishes the job.
            onToggle={() => setPanel({ id: menu.id, open: !isThisMenu })}
            onClose={close}
            onExited={() => setPanel(CLOSED)}
          />
        );
      })}
      <StyledAnchor href={PRICING_LINK.href} sx={{ ...TOP_LEVEL, maxWidth: 1 }}>
        {PRICING_LINK.label}
      </StyledAnchor>
    </Box>
  );
}
